import React, { useCallback, useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import CtaButton from "../ui/CtaButton";
import type { GlassShape } from "../../lib/liquid-glass/engine";
import {
  Menu,
  X,
  User,
  CreditCard,
  Lightbulb,
  LifeBuoy,
  Inbox,
  LogOut,
  LogIn,
  LucideIcon,
} from "lucide-react";
import { openSupport } from "../../features/support/supportBus";
import { useUser } from "../login/UserContext";
import AuthenticationButton from "../login/AuthenticationButton";
import { openSignInModal } from "../login/signInModalSignal";
import LiveNowPill from "../live/LiveNowPill";
import ActiveUsersPill from "../../features/admin/components/ActiveUsersPill";
import BottomNav from "./BottomNav";
import {
  NAV_SECTIONS,
  NavSection,
  isImmersiveRoute,
} from "./navConfig";
import { isUnpaidRole } from "../payments/pricing-routing";
import { useScrollLock } from "../../hooks/useScrollLock";
// Icon-only Brawler mark (the 1.0 logo, cropped + optimized from the source
// SVG to a small transparent PNG) — used in the mobile menu header.
import brawlerMark from "./brawler-mark.png";
import "./app-nav.css";

/** Flat active marker behind the selected nav item. Was a WebGL liquid-glass
 *  skin; a solid tonal plate reads sturdier. Args kept so call sites match. */
const navActiveGlass = (
  isActive: boolean,
  _shape: GlassShape = "pill",
  _radius = 12
) =>
  isActive ? <span className="bb-nav__glass bb-nav__active" aria-hidden="true" /> : null;

const LOGO_SRC =
  "https://storage.googleapis.com/assestsbookiebrawler/logos/BOOKIE%20BRAWLER%20(6).png";

/** Remembers whether the desktop rail is pinned open across reloads. */
const PIN_KEY = "bb-nav-rail-pinned";

/**
 * Account destinations mirrored from ProfileIcon's dropdown so the mobile
 * menu carries the same links the desktop profile avatar does. The product
 * links there (Positive EV, Arbitrage, …) are already covered by the nav
 * sections; these are the account-only extras.
 */
const ACCOUNT_LINKS: { to: string; label: string; icon: LucideIcon }[] = [
  { to: "/account-settings", label: "Profile", icon: User },
  { to: "/pricing", label: "Pricing & Plans", icon: CreditCard },
  { to: "/suggestions", label: "Suggestions", icon: Lightbulb },
  { to: "/support", label: "My requests", icon: Inbox },
];

interface AppNavProps {
  /** Sign the user out (wired up by App). */
  onLogout: () => void;
  /** Open the sign-in modal (wired up by App). */
  toggleModal: () => void;
}

/**
 * AppNav — the OddsJam-style application shell navigation.
 *
 *  • Top bar (all sizes): hamburger lines left of the logo, with the
 *    account / CTA cluster on the right.
 *  • Desktop (≥1024px): a fixed left rail of nav icons that the top-bar
 *    hamburger collapses/expands (and that peeks open on hover). Help and
 *    sign-out sit at its foot.
 *  • Mobile (<1024px): a five-icon bottom bar (BottomNav) carries primary
 *    navigation, notifications, and help; the top-right hamburger opens a
 *    drawer with the full nav + profile links. Navigation lives at the
 *    bottom only — the old product tab strip under the top bar is gone, so
 *    a phone never wears two nav bars at once.
 *
 * Every surface is driven by the single config in navConfig.tsx.
 */
const AppNav: React.FC<AppNavProps> = ({ onLogout, toggleModal }) => {
  const { user, authReady } = useUser();
  const location = useLocation();
  const isHomepage = location.pathname === "/";
  useEffect(() => {
    document.documentElement.classList.toggle("bb-homepage", isHomepage);
    return () => document.documentElement.classList.remove("bb-homepage");
  }, [isHomepage]);

  const isAdmin =
    authReady && user?.role?.toString().toUpperCase() === "ADMIN";
  const isAuthed = authReady && !!user && user.id !== 0;
  const showTrialCta =
    authReady && (!isAuthed || isUnpaidRole(user?.role));

  // Drop admin-only entries for everyone else, then prune empty groups so
  // the trailing "Admin" section doesn't leave a dangling divider.
  const sections: NavSection[] = NAV_SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter((item) => !item.admin || isAdmin),
  })).filter((section) => section.items.length > 0);

  // ── Desktop rail pin state (persisted) ──────────────────────────────
  const [pinned, setPinned] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem(PIN_KEY);
      // Default the desktop rail to expanded so labels and the full logo are
      // visible out of the box (OddsJam-style persistent sidebar). Respect an
      // explicit collapse choice once the user has actually toggled it.
      return stored === null ? true : stored === "1";
    } catch {
      return true;
    }
  });
  const togglePin = useCallback(() => {
    setPinned((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(PIN_KEY, next ? "1" : "0");
      } catch {
        /* private mode — ignore */
      }
      return next;
    });
  }, []);

  // Mirror the pinned state onto <html> so the content wrapper can push
  // over (margin-left) only when pinned — hover-peek stays an overlay.
  useEffect(() => {
    document.documentElement.classList.toggle("bb-rail-pinned", pinned);
    return () => {
      document.documentElement.classList.remove("bb-rail-pinned");
    };
  }, [pinned]);

  // ── Mobile menu (top-right dropdown) ────────────────────────────────
  const [menuOpen, setMenuOpen] = useState(false);
  const closeMenu = useCallback(() => setMenuOpen(false), []);
  const toggleMenu = useCallback(() => setMenuOpen((p) => !p), []);

  // Close the menu whenever navigation happens (link tap or back btn).
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // iOS-safe background scroll lock while the mobile menu is open. (Plain
  // overflow:hidden on body does not stop touch scroll on iOS Safari — the
  // page kept scrolling under the open drawer.) The menu only opens on mobile,
  // so this is effectively mobile-only.
  useScrollLock(menuOpen);

  // Escape-to-close while the menu is open.
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  // Immersive detail routes (feed thread) supply their own top header on small
  // screens, so the app top bar stands down there. The <html> flag zeroes
  // --bb-topbar-h, which collapses every offset derived from it (content
  // padding, sticky headers, dropdown anchors). The bottom bar stays put.
  const immersive = isImmersiveRoute(location.pathname);
  useEffect(() => {
    document.documentElement.classList.toggle("bb-immersive", immersive);
    return () => {
      document.documentElement.classList.remove("bb-immersive");
    };
  }, [immersive]);

  // Sign out the same way ProfileIcon does: clear auth, then land on home.
  const handleSignOut = useCallback(() => {
    setMenuOpen(false);
    onLogout();
    window.location.href = "/";
  }, [onLogout]);

  const handleSignIn = useCallback(() => {
    setMenuOpen(false);
    openSignInModal();
  }, []);

  const isPricingPath =
    location.pathname === "/pricing" || location.pathname.startsWith("/pricing/");

  const renderPricingNavRow = (
    variant: "menu" | "rail",
    onNavigate?: () => void,
  ) => {
    const isMenu = variant === "menu";
    const linkClass = isMenu ? "bb-menu__link" : "bb-rail__link";
    const iconClass = isMenu ? "bb-menu__icon" : "bb-rail__icon";
    const labelClass = isMenu ? "" : "bb-rail__label";
    const rowClass = isMenu ? "bb-menu__pricing-row" : "bb-rail__pricing-row";
    const trialClass = isMenu ? "bb-menu__trial-cta" : "bb-rail__trial-cta";

    return (
      <div
        className={`bb-nav-pricing-row ${rowClass}${isPricingPath ? " is-active" : ""}`}
        key="pricing-row"
      >
        <NavLink
          to="/pricing"
          end
          onClick={onNavigate}
          className={`${linkClass} bb-nav-pricing-main`}
        >
          {navActiveGlass(isPricingPath, "rounded", 12)}
          <span className={iconClass}>
            <CreditCard size={20} strokeWidth={1.9} />
          </span>
          <span className={labelClass}>Pricing &amp; Plans</span>
        </NavLink>
        {showTrialCta && (
          <CtaButton
            href="/pricing"
            onClick={onNavigate}
            glassClassName={trialClass}
            className="bb-nav-trial-cta__inner"
          >
            Try for free
          </CtaButton>
        )}
      </div>
    );
  };

  return (
    <>
      {/* ─────────────────────────── TOP BAR ──────────────────────────── */}
      <header className={`bb-topbar${immersive ? " bb-topbar--immersive" : ""}`}>
        {/* Desktop: hamburger lines collapse/expand the rail. */}
        <button
          type="button"
          className="bb-topbar__rail-toggle"
          onClick={togglePin}
          aria-pressed={pinned}
          aria-label={pinned ? "Collapse sidebar" : "Expand sidebar"}
          title={pinned ? "Collapse sidebar" : "Expand sidebar"}
        >
          <Menu size={22} />
        </button>

        <Link to="/" className="bb-topbar__brand" aria-label="BookieBrawler home">
          <img src={LOGO_SRC} alt="BookieBrawler" />
        </Link>

        <div className="bb-topbar__right">
          <LiveNowPill />
          {isAdmin && (
            <div className="bb-topbar__admin">
              <ActiveUsersPill compact intervalMs={30000} popoverAlign="right" />
            </div>
          )}
          {/* Desktop: full auth CTA / profile avatar. */}
          <div className="bb-topbar__auth">
            <AuthenticationButton onLogout={onLogout} toggleModal={toggleModal} />
          </div>
          {/* Mobile: a single button that drops the menu open on the right. */}
          <button
            type="button"
            className="bb-topbar__menu"
            onClick={toggleMenu}
            aria-label="Open menu"
            aria-expanded={menuOpen}
          >
            {menuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </header>

      {/* ───────────────────────── DESKTOP RAIL ───────────────────────── */}
      <aside
        className={`bb-rail${pinned ? " is-pinned" : ""}`}
        aria-label="Primary"
      >
        <nav className="bb-rail__nav">
          {sections.map((section, i) => (
            <div className="bb-rail__group" key={section.title ?? `g${i}`}>
              {i > 0 && <div className="bb-rail__divider" aria-hidden="true" />}
              {section.title && (
                <div className="bb-rail__eyebrow">{section.title}</div>
              )}
              {section.items.map((item) => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    className={({ isActive }) =>
                      `bb-rail__link${isActive ? " is-active" : ""}`
                    }
                  >
                    {({ isActive }) => (
                      <>
                        {navActiveGlass(isActive, "rounded", 12)}
                        <span className="bb-rail__icon">
                          <Icon size={21} strokeWidth={1.9} />
                        </span>
                        <span className="bb-rail__label">{item.label}</span>
                      </>
                    )}
                  </NavLink>
                );
              })}
            </div>
          ))}

          {/* Account + pricing — visible to everyone; profile only when signed in. */}
          <div className="bb-rail__group">
            <div className="bb-rail__divider" aria-hidden="true" />
            {renderPricingNavRow("rail")}
            {isAuthed && (
              <NavLink
                to="/account-settings"
                className={({ isActive }) =>
                  `bb-rail__link${isActive ? " is-active" : ""}`
                }
              >
                <span className="bb-rail__icon">
                  <User size={21} strokeWidth={1.9} />
                </span>
                <span className="bb-rail__label">Profile</span>
              </NavLink>
            )}
          </div>
        </nav>

        {/* Sign out occupies this slot for now; the promo card returns later. */}
        <div className="bb-rail__promo">
          {/* Help lives in the rail rather than in a launcher floating over
              the page: the rail is always on screen on desktop, so the icon is
              just as reachable and the content keeps its corner. It sits in
              the foot, outside the scrolling nav, so a long tool list can
              never push it below the fold. Opens the same support sheet the
              mobile bottom bar does. */}
          {isAuthed && (
            <button
              type="button"
              className="bb-rail__link bb-rail__help"
              onClick={() => openSupport()}
            >
              <span className="bb-rail__icon">
                <LifeBuoy size={21} strokeWidth={1.9} />
              </span>
              <span className="bb-rail__label">Help &amp; support</span>
            </button>
          )}
          {isAuthed ? (
            <button
              type="button"
              className="bb-rail__promo-card bb-rail__signout-card"
              onClick={handleSignOut}
            >
              <span className="bb-rail__promo-icon bb-rail__signout-icon">
                <LogOut size={19} strokeWidth={2} />
              </span>
              <span className="bb-rail__promo-text">
                <span className="bb-rail__promo-title">Sign Out</span>
                <span className="bb-rail__promo-sub">End session</span>
              </span>
            </button>
          ) : (
            <button
              type="button"
              className="bb-rail__promo-card bb-rail__signout-card"
              onClick={handleSignIn}
            >
              <span className="bb-rail__promo-icon bb-rail__signout-icon">
                <LogIn size={19} strokeWidth={2} />
              </span>
              <span className="bb-rail__promo-text">
                <span className="bb-rail__promo-title">Sign in</span>
                <span className="bb-rail__promo-sub">Access your tools</span>
              </span>
            </button>
          )}
        </div>
      </aside>

      {/* ─────────────────────── MOBILE BOTTOM BAR ────────────────────── */}
      <BottomNav />

      {/* ───────────────── MOBILE RIGHT-HAND DROPDOWN MENU ────────────── */}
      <div
        className={`bb-menu-root${menuOpen ? " is-open" : ""}`}
        aria-hidden={!menuOpen}
      >
        <div className="bb-menu__scrim" onClick={closeMenu} />
        <div className="bb-menu" role="menu" aria-label="Menu">
          <div className="bb-menu__head">
            <Link
              to="/"
              className="bb-menu__brand"
              onClick={closeMenu}
              aria-label="BookieBrawler home"
            >
              <img src={brawlerMark} alt="BookieBrawler" />
            </Link>
            <button
              type="button"
              className="bb-menu__close"
              onClick={closeMenu}
              aria-label="Close menu"
            >
              <X size={22} />
            </button>
          </div>
          <nav className="bb-menu__nav">
            {sections.map((section, i) => (
              <div className="bb-menu__group" key={section.title ?? `mg${i}`}>
                {section.title && (
                  <div className="bb-menu__eyebrow">{section.title}</div>
                )}
                {section.items.map((item) => {
                  const Icon = item.icon;
                  return (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      end={item.end}
                      onClick={closeMenu}
                      className={({ isActive }) =>
                        `bb-menu__link${isActive ? " is-active" : ""}`
                      }
                    >
                      {({ isActive }) => (
                        <>
                          {navActiveGlass(isActive, "rounded", 11)}
                          <span className="bb-menu__icon">
                            <Icon size={20} strokeWidth={1.9} />
                          </span>
                          <span>{item.label}</span>
                        </>
                      )}
                    </NavLink>
                  );
                })}
              </div>
            ))}

            {/* Account block — the profile-menu links. */}
            <div className="bb-menu__group">
              <div className="bb-menu__eyebrow">Account</div>
              {renderPricingNavRow("menu", closeMenu)}
              {ACCOUNT_LINKS.filter((item) => item.to !== "/pricing").map((item) => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    onClick={closeMenu}
                    className={({ isActive }) =>
                      `bb-menu__link${isActive ? " is-active" : ""}`
                    }
                  >
                    {({ isActive }) => (
                      <>
                        {navActiveGlass(isActive, "rounded", 11)}
                        <span className="bb-menu__icon">
                          <Icon size={20} strokeWidth={1.9} />
                        </span>
                        <span>{item.label}</span>
                      </>
                    )}
                  </NavLink>
                );
              })}
              {isAuthed && (
                <button
                  type="button"
                  className="bb-menu__link"
                  onClick={() => {
                    closeMenu();
                    openSupport();
                  }}
                >
                  <span className="bb-menu__icon">
                    <LifeBuoy size={20} strokeWidth={1.9} />
                  </span>
                  <span>Help &amp; support</span>
                </button>
              )}
            </div>
          </nav>

          <div className="bb-menu__foot">
            {isAuthed ? (
              <button
                type="button"
                className="bb-menu__signout"
                onClick={handleSignOut}
              >
                <LogOut size={18} />
                <span>Sign Out</span>
              </button>
            ) : (
              <button
                type="button"
                className="bb-menu__cta"
                onClick={handleSignIn}
              >
                <LogIn size={18} />
                <span>Sign in</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default AppNav;
